import axios from 'axios';
import { CarPayload, BudgetPayload } from '../interfaces/Car'

import { CarsResponse, StatusHistoryResponse } from '../responses/CarResponse';

const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const createCar = async (payload: CarPayload) => {
	try {
		const response = await axios.post(
			`${process.env.REACT_APP_BACKEND_URL}/cars`,
			payload,
			{
				withCredentials: true,
        headers: authHeader(),
			}
		);
		return response.data;
	} catch (error: unknown) {
		if (axios.isAxiosError(error)) {
			throw new Error(error.response?.data?.message || 'Erro ao criar o carro');
		} else {
			throw new Error('Erro desconhecido');
		}
	}

};

export const fetchCars = async (
  page: number = 1,
  limit: number = 10,
  sortBy: string = 'createdAt',
  sortOrder: 'asc' | 'desc' = 'desc',
) => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/cars`, {
      params: { page, limit, sortBy, sortOrder },
      withCredentials: true,
    headers: authHeader(),
    });

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar carros');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const fetchCarsNoPagination = async () => {
  try {
    const response = await axios.get<CarsResponse>(
      `${process.env.REACT_APP_BACKEND_URL}/cars`,
      {
        withCredentials: true,
		headers: authHeader(),
      }
    );

    return response.data.cars;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao obter a lista de carros');
    } else {
      throw new Error('Erro desconhecido');
    }
  }

};

export const fetchCarsDiffThanEntrega = async (
  page: number = 1,
  limit: number = 10,
  sortBy: string = 'createdAt',
  sortOrder: 'asc' | 'desc' = 'desc',
  responsibleName?: string,
) => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/cars/non-entrega`, {
      params: { page, limit, sortBy, sortOrder, responsibleName },
      withCredentials: true,
		headers: authHeader(),
    });

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar carros');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

/** Lista os atendimentos encerrados (esteira ENTREGA), com busca por placa, proprietário ou nº do evento. */
export const fetchDeliveredCars = async (
  page: number = 1,
  limit: number = 10,
  sortBy: string = 'statusUpdatedAt',
  sortOrder: 'asc' | 'desc' = 'desc',
  search?: string,
  /** at-134: só os atendimentos com telefone e sem pesquisa de satisfação enviada. */
  semPesquisa = false,
) => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/cars/entrega`, {
      params: { page, limit, sortBy, sortOrder, search: search || undefined, semPesquisa: semPesquisa ? 'true' : undefined },
      withCredentials: true,
      headers: authHeader(),
    });

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar carros entregues');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const fetchCarsOnlyOrcamento = async (
  page: number = 1,
  limit: number = 10,
  sortBy: string = 'createdAt',
  sortOrder: 'asc' | 'desc' = 'desc',
) => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/cars/only-orcamento`, {
      params: { page, limit, sortBy, sortOrder },
      withCredentials: true,
    });

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar carros');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const getCar = async (id: string) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/cars/${id}`,
      {
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao obter os dados do carro');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const updateCar = async (id: string, payload: CarPayload) => {
  try {

    const response = await axios.patch(
      `${process.env.REACT_APP_BACKEND_URL}/cars/${id}`,
      payload,
      {
        withCredentials: true,
      }
    );

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao atualizar os dados do carro');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

/**
 * Aprova/rejeita a mudança de esteira pendente. `desfechoServicoAtual` (at-46) declara o
 * desfecho do serviço da esteira ATUAL quando a aprovação move o veículo (ver updateCarStatus).
 *
 * `confirmarDesvinculoInelegiveis` (at-62) é o aceite de que os colaboradores sem a especialidade
 * da esteira aprovada saem do veículo. Sem ele, o backend devolve `409 COLABORADORES_INELEGIVEIS`
 * e NADA é alterado — a tela descreve o efeito e reenvia com o aceite.
 */
export const approveCarStatus = async (
  carId: string,
  estimatedDaysInShop?: number,
  approve: boolean = true,
  forceCloseActiveSessions?: boolean,
  desfechoServicoAtual?: 'CONCLUIDO' | 'PAUSADO',
  confirmarDesvinculoInelegiveis?: boolean,
  /** at-134 (ADR 0065): o motivo da parada, só quando o desfecho é PAUSADO. */
  motivoDaParada?: { motivoParadaId?: string; pausaObservacao?: string },
) => {
  try {
    const requestBody = {
      approve,
      ...(estimatedDaysInShop !== undefined && { estimatedDaysInShop }),
      ...(forceCloseActiveSessions !== undefined && { forceCloseActiveSessions }),
      ...(desfechoServicoAtual !== undefined && { desfechoServicoAtual }),
      ...(confirmarDesvinculoInelegiveis !== undefined && { confirmarDesvinculoInelegiveis }),
      ...(desfechoServicoAtual === 'PAUSADO' && motivoDaParada?.motivoParadaId
        ? { motivoParadaId: motivoDaParada.motivoParadaId }
        : {}),
      ...(desfechoServicoAtual === 'PAUSADO' && motivoDaParada?.pausaObservacao
        ? { pausaObservacao: motivoDaParada.pausaObservacao }
        : {}),
    };

    const response = await axios.patch(
      `${process.env.REACT_APP_BACKEND_URL}/cars/${carId}/approve-status`,
      requestBody,
      { withCredentials: true }
    );

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw error;
    } else {
      throw new Error('Erro desconhecido. Por favor, entre em contato com o TI.');
    }
  }
};

/**
 * at-46: o Chefe fecha uma pendência de serviço inacabado que não será retomada.
 * O serviço passa a CONCLUIDO sem o veículo precisar voltar à esteira (fica na Auditoria).
 */
export const concluirServicoInacabado = async (carId: string, esteiraId: string) => {
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_BACKEND_URL}/cars/${carId}/atividades/${esteiraId}/concluir`,
      {},
      { withCredentials: true }
    );

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw error;
    } else {
      throw new Error('Erro desconhecido. Por favor, entre em contato com o TI.');
    }
  }
};

export const assignCarResponsible = async (carId: string, userId: string) => {
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_BACKEND_URL}/cars/${carId}/assign-responsible`,
      { userId },
      { withCredentials: true },
    );
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao atribuir colaborador ao carro');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

/**
 * Muda a esteira do veículo (ou cria a solicitação, para quem só pode solicitar).
 * `desfechoServicoAtual` (at-46) declara o que acontece com o serviço da esteira ATUAL quando o
 * veículo sai dela: 'CONCLUIDO' (realizado) ou 'PAUSADO' (fica inacabado, retomável na volta).
 * Ausente = 'CONCLUIDO' no backend.
 *
 * `confirmarDesvinculoInelegiveis` (at-62) aceita que os colaboradores sem a especialidade da
 * esteira DESTINO saiam do veículo. Sem ele, o backend devolve `409 COLABORADORES_INELEGIVEIS`
 * (com as sessões ativas no mesmo payload) e não altera nada.
 */
export const updateCarStatus = async (
  carId: string,
  esteiraId: string,
  estimatedDaysInStatus?: number,
  forceCloseActiveSessions?: boolean,
  desfechoServicoAtual?: 'CONCLUIDO' | 'PAUSADO',
  confirmarDesvinculoInelegiveis?: boolean,
  /** at-134 (ADR 0065): o motivo da parada, só quando o desfecho é PAUSADO. */
  motivoDaParada?: { motivoParadaId?: string; pausaObservacao?: string },
) => {
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_BACKEND_URL}/cars/${carId}/status`,
      {
        esteiraId,
        ...(estimatedDaysInStatus !== undefined && { estimatedDaysInStatus }),
        ...(forceCloseActiveSessions !== undefined && { forceCloseActiveSessions }),
        ...(desfechoServicoAtual !== undefined && { desfechoServicoAtual }),
        ...(confirmarDesvinculoInelegiveis !== undefined && { confirmarDesvinculoInelegiveis }),
        ...(desfechoServicoAtual === 'PAUSADO' && motivoDaParada?.motivoParadaId
          ? { motivoParadaId: motivoDaParada.motivoParadaId }
          : {}),
        ...(desfechoServicoAtual === 'PAUSADO' && motivoDaParada?.pausaObservacao
          ? { pausaObservacao: motivoDaParada.pausaObservacao }
          : {}),
      },
      { withCredentials: true }
    );

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw error;
    } else {
      throw new Error('Erro desconhecido. Por favor, entre em contato com o TI.');
    }
  }
};

export const searchCars = async (
  query?: string,
  page: number = 1,
  limit: number = 10,
  sortBy: string = 'createdAt',
  sortOrder: 'asc' | 'desc' = 'desc',
  responsibleName?: string,
  /**
   * at-65 (BUG-V12): inclui atendimentos encerrados no resultado. Só a Tela de Controle
   * passa `true` — a listagem e o filtro dela já mostram entregues, e sem isto a busca
   * pela própria placa devolvia "nenhum resultado" para um veículo visível na tela.
   * Recepção e Administração não enviam, e seguem sem entregues.
   */
  incluirEntrega?: boolean,
) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/cars/search`,
      {
        params: {
          query,
          page,
          limit,
          sortBy,
          sortOrder,
          responsibleName,
          ...(incluirEntrega ? { incluirEntrega: 'true' } : {}),
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar carros');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const searchCarsOrcamento = async (
  query?: string,
  page: number = 1,
  limit: number = 10,
  sortBy: string = 'createdAt',
  sortOrder: 'asc' | 'desc' = 'desc',
  status: string = 'ORÇAMENTO'
) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/cars/search`,
      {
        params: {
          query,
          page,
          limit,
          sortBy,
          sortOrder,
          status,
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar carros');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const filterCars = async (
  carClass?: string | string[],
  carStatus?: string | string[],
  pendingStatus?: string | string[],
  approvalStatus?: string | string[],
  damageType?: string | string[],
  page = 1,
  limit = 10,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc'
) => {
  try {
    const params = {
      carClass: Array.isArray(carClass) ? carClass.join(',') : carClass,
      carEsteiraId: Array.isArray(carStatus) ? carStatus.join(',') : carStatus,
      pendingEsteiraId: Array.isArray(pendingStatus) ? pendingStatus.join(',') : pendingStatus,
      approvalStatus: Array.isArray(approvalStatus) ? approvalStatus.join(',') : approvalStatus,
      damageType: Array.isArray(damageType) ? damageType.join(',') : damageType,
      page,
      limit,
      sortBy,
      sortOrder,
    };

    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/cars/filter`,
      {
        params,
        withCredentials: true,
      }
    );

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar carros com filtros');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const filterCarsNonEntrega = async (
  carClass?: string | string[],
  carStatus?: string | string[],
  pendingStatus?: string | string[],
  approvalStatus?: string | string[],
  damageType?: string | string[],
  page = 1,
  limit = 10,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc',
  releaseStatus?: string[]
) => {
  try {
    const params: any = {
      carClass: Array.isArray(carClass) ? carClass.join(',') : carClass,
      carEsteiraId: Array.isArray(carStatus) ? carStatus.join(',') : carStatus,
      pendingEsteiraId: Array.isArray(pendingStatus) ? pendingStatus.join(',') : pendingStatus,
      approvalStatus: Array.isArray(approvalStatus) ? approvalStatus.join(',') : approvalStatus,
      damageType: Array.isArray(damageType) ? damageType.join(',') : damageType,
      page,
      limit,
      sortBy,
      sortOrder,
    };

    if (releaseStatus !== undefined) {
      params.releaseStatus = Array.isArray(releaseStatus) ? releaseStatus.join(',') : releaseStatus;
    }

    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/cars/filter-non-entrega`,
      {
        params,
        withCredentials: true,
      }
    );

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar carros com filtros');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const filterCarsNonEntregaOrcamento = async (
  carClass?: string | string[],
  pendingStatus?: string | string[],
  approvalStatus?: string | string[],
  damageType?: string | string[],
  page = 1,
  limit = 10,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc'
) => {
  try {
    const params = {
      carClass: Array.isArray(carClass) ? carClass.join(',') : carClass,
      carEsteiraId: 'est-orcamento',
      pendingEsteiraId: Array.isArray(pendingStatus) ? pendingStatus.join(',') : pendingStatus,
      approvalStatus: Array.isArray(approvalStatus) ? approvalStatus.join(',') : approvalStatus,
      damageType: Array.isArray(damageType) ? damageType.join(',') : damageType,
      page,
      limit,
      sortBy,
      sortOrder,
    };

    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/cars/filter-non-entrega`,
      {
        params,
        withCredentials: true,
      }
    );

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar carros com filtros');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const updateCarBudget = async (id: string, payload: BudgetPayload) => {
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_BACKEND_URL}/cars/${id}/orcamento`,
      payload,
      {
        withCredentials: true,
      }
    );

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao atualizar datas de orçamento do carro');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const updateCarFullCompensation = async (id: string, payload: { fullCompensation: boolean }) => {
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_BACKEND_URL}/cars/${id}/full-compensation`,
      payload,
      {
        withCredentials: true,
      }
    );

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao atualizar o status de indenização total do carro');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const updateCarPartsTrackingStatus = async (
  id: string,
  payload: { partsTrackingStatus: 'EM_COTACAO' | 'CONCLUIDO' }
) => {
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_BACKEND_URL}/cars/${id}/parts-tracking-status`,
      payload,
      {
        withCredentials: true,
        headers: authHeader(),
      }
    );

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          'Erro ao atualizar o status de acompanhamento de peças do carro'
      );
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export interface KanbanReguaItem {
  carId: string;
  /** Esteiras planejadas (atividades ativas). */
  planejadas: string[];
  /** Esteiras que já tiveram período aprovado. */
  aprovadas: string[];
  /**
   * 🔴 at-99: esteiras cujo serviço foi REALIZADO — as aprovadas mais os serviços concluídos.
   *
   * É este o campo que a régua deve usar. Serviço concluído numa esteira aberta em PARALELO
   * nunca gera período no histórico de status (o veículo não passou por aquela etapa), então
   * `aprovadas` sozinho o mostraria como "planejado e não feito" para sempre.
   *
   * Opcional para a tela sobreviver a um backend anterior à entrega.
   */
  realizadas?: string[];
  /** Última esteira aprovada, ou null quando o veículo ainda não passou por nenhuma. */
  ultimaAprovada: string | null;
  /** at-99: esteiras abertas em paralelo agora — o chip do card do kanban. */
  esteirasParalelas?: Array<{
    esteiraId: string;
    nome: string;
    cor: string | null;
    abertaEm: string;
  }>;
}

/**
 * Régua de esteiras do kanban da Home para vários carros numa consulta (at-65, BUG-V35).
 *
 * Substitui as **2 requisições por veículo** que a Home fazia (`status-history` +
 * `atividade-carro`): com 40 cards eram ~80 chamadas na primeira carga, refeitas a cada
 * troca de página.
 */
export const getKanbanRegua = async (carIds: string[]): Promise<KanbanReguaItem[]> => {
  const ids = Array.from(new Set((carIds || []).filter(Boolean)));
  if (ids.length === 0) return [];

  try {
    const response = await axios.get<{ itens: KanbanReguaItem[] }>(
      `${process.env.REACT_APP_BACKEND_URL}/cars/kanban-regua`,
      {
        params: { carIds: ids.join(',') },
        withCredentials: true,
        headers: authHeader(),
      }
    );
    return response.data?.itens ?? [];
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao obter a régua de esteiras dos veículos');
    }
    throw new Error('Erro desconhecido');
  }
};

export const getCarStatusHistory = async (id: string, page: number = 1, limit: number = 999) => {
  try {
    const response = await axios.get<StatusHistoryResponse>(
      `${process.env.REACT_APP_BACKEND_URL}/cars/${id}/status-history`,
      {
        params: { page, limit },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao obter histórico de status do carro');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const getCarResponsiblesHistory = async (id: string, page: number = 1, limit: number = 10) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/cars/${id}/responsibles-history`,
      {
        params: { page, limit },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao obter histórico de responsáveis');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const updateCarBudgetValue = async (id: string, budgetValue: number) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/cars/${id}/budget`,
      { budgetValue },
      {
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar o valor do orçamento',
      );
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const updateEstimatedDaysInShop = async (id: string, estimatedDaysInStatus: number) => {
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_BACKEND_URL}/cars/${id}/estimated-days`,
      { estimatedDaysInStatus },
      {
        withCredentials: true,
        headers: authHeader(),
      }
    );
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar os dias estimados na esteira'
      );
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const updateCarBudgetDecision = async (
  id: string,
  status: 'APROVAR_ORCAMENTO_DE_COMPRA' | 'ORCAMENTO_APENAS' | 'NAO_APROVAR_ORCAMENTO'
) => {
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_BACKEND_URL}/cars/${id}/budget-decision`,
      { status },
      {
        withCredentials: true,
        headers: authHeader(),
      },
    );

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao confirmar tipo do orçamento');
    }

    throw new Error('Erro desconhecido');
  }
};

export const getCarBudgetDecision = async (id: string) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/cars/${id}/budget-decision`,
      {
        withCredentials: true,
        headers: authHeader(),
      },
    );

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao obter status do orçamento');
    }

    throw new Error('Erro desconhecido');
  }
};

export const releaseCar = async (id: string, payload: { released: boolean; esteiraIds?: string[]; note?: string }) => {
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_BACKEND_URL}/cars/${id}/release`,
      payload,
      {
        withCredentials: true,
        headers: authHeader(),
      }
    );

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao atualizar a liberação do carro');
    }

    throw new Error('Erro desconhecido');
  }
};

export const fetchCarsByPlate = async (plate: string) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/cars/by-plate/${plate}`,
      {
        withCredentials: true,
        headers: authHeader(),
      }
    );
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao obter dados do veículo pela placa');
    }
    throw new Error('Erro desconhecido');
  }
};

